import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useUpdateSecurityIssueByDisplayId } from "@/hooks/useSecurityIssues";
import type { IssueStatus, SecurityIssue } from "@/types/security";
import { cn } from "@/lib/utils";
import { getIssueStatusColor } from "@/utils/securityHelpers";

const STATUS_OPTIONS: { value: IssueStatus; label: string }[] = [
  { value: "OPEN", label: "Open" },
  { value: "IN_REVIEW", label: "In Review" },
  { value: "RESOLVED", label: "Resolved" },
  { value: "FALSE_POSITIVE", label: "False Positive" },
  { value: "SUPPRESSED", label: "Suppressed" },
];

interface IssueStatusSelectProps {
  issue: SecurityIssue;
}

export function IssueStatusSelect({ issue }: IssueStatusSelectProps) {
  const mutation = useUpdateSecurityIssueByDisplayId();

  // Derived state pattern: show optimistic value while mutation is pending
  const currentStatus =
    mutation.isPending && mutation.variables?.data.status
      ? mutation.variables.data.status
      : issue.status;

  const handleStatusChange = (newStatus: string) => {
    if (newStatus === issue.status) return;

    mutation.mutate({
      displayId: issue.id,
      data: { status: newStatus as IssueStatus },
    });
  };

  return (
    <Select value={currentStatus} onValueChange={handleStatusChange}>
      <SelectTrigger
        className={cn(
          "w-auto h-auto border rounded-full px-2.5 py-0.5 text-xs font-semibold gap-1",
          getIssueStatusColor(currentStatus),
          mutation.isPending && "opacity-70"
        )}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {STATUS_OPTIONS.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            <div className="flex items-center gap-2">
              <Badge
                variant="outline"
                className={cn(
                  "pointer-events-none",
                  getIssueStatusColor(option.value)
                )}
              >
                {option.label}
              </Badge>
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
